'use client';

import React, { useState, useEffect, useRef } from 'react';
import DashboardTopbar from './components/DashboardTopbar';
import MetricsBentoGrid from './components/MetricsBentoGrid';
import LiveWaveformChart from './components/LiveWaveformChart';
import ChannelQualityGrid from './components/ChannelQualityGrid';
import ArtifactEventFeed from './components/ArtifactEventFeed';
import BandPowerTimeline from './components/BandPowerTimeline';
import SessionProvider from './components/SessionProvider';
import LiveEEGIndicator from './components/LiveEEGIndicator';
import HistoricalTrendsChart from './components/HistoricalTrendsChart';
import IBrainLoader from './components/IBrainLoader';
import CollapsibleSection from './components/CollapsibleSection';
import CognitiveInsightsPanel from './components/CognitiveInsightsPanel';
import SessionRecordingControls from './components/SessionRecordingControls';
import AlzheimerBiomarkerPanel from './components/AlzheimerBiomarkerPanel';
import BrainHealthQuiz from './components/BrainHealthQuiz';
import LiveElectrodeMap from './components/LiveElectrodeMap';
import GammaBandPanel from './components/GammaBandPanel';
import ConnectivityCoherenceMap from './components/ConnectivityCoherenceMap';
import OnboardingTour from './components/OnboardingTour';
import AIAnalysisPanel from './components/AIAnalysisPanel';
import ADHDRiskPanel from './components/ADHDRiskPanel';
import LeaderboardStreakPanel from './components/LeaderboardStreakPanel';
import SessionHistoryPanel from './components/SessionHistoryPanel';
import NeuroTrainer from './components/NeuroTrainer';
import AdaptiveBrainGym from './components/AdaptiveBrainGym';
import LongitudinalAnalytics from './components/LongitudinalAnalytics';
import PostDiagnosticPrescription from './components/PostDiagnosticPrescription';
import AdaptiveProtocolTracker from './components/AdaptiveProtocolTracker';
import ClinicianReportExport from './components/ClinicianReportExport';
import BiomarkerNotificationSystem from './components/BiomarkerNotificationSystem';
import { AIPrescription } from '@/lib/services/adaptiveProtocolService';
import PPGMonitor from './components/PPGMonitor';
import EEGChannelAlignmentModule from './components/EEGChannelAlignmentModule';
import MobileBottomNav from '@/components/ui/MobileBottomNav';
import { useAuth } from '@/contexts/AuthContext';
import AlertThresholdsConfig from './components/AlertThresholdsConfig';
import { useRouter } from 'next/navigation';
import Breadcrumb from '@/components/ui/Breadcrumb';
import DailyBrainCheck from './components/DailyBrainCheck';
import SessionProgressComparison from './components/SessionProgressComparison';

// ─── Neural Canvas Background ────────────────────────────────────────────────
function NeuralCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let t = 0;
    let paused = false;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Pause animation when tab is hidden (performance)
    const handleVisibility = () => {
      paused = document.hidden;
      if (!paused) draw();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Neuron nodes
    const NODE_COUNT = 28;
    interface Node {
      x: number; y: number;
      vx: number; vy: number;
      r: number; pulse: number; pulseSpeed: number;
    }
    const nodes: Node[] = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: 2 + Math.random() * 3,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: 0.02 + Math.random() * 0.03,
    }));

    const draw = () => {
      if (paused) return;
      t += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Move nodes
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        n.pulse += n.pulseSpeed;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      // Draw synaptic connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 180) {
            const alpha = (1 - dist / 180) * 0.18;
            // Pulse signal along connection
            const signalPos = (Math.sin(t * 2 + i * 0.7) + 1) / 2;
            const sx = nodes[i].x + (nodes[j].x - nodes[i].x) * signalPos;
            const sy = nodes[i].y + (nodes[j].y - nodes[i].y) * signalPos;

            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(249,115,22,${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();

            // Traveling signal dot
            if (dist < 140) {
              ctx.beginPath();
              ctx.arc(sx, sy, 1.5, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(249,115,22,${alpha * 3})`;
              ctx.fill();
            }
          }
        }
      }

      // Draw neuron nodes
      nodes.forEach((n, i) => {
        const glow = (Math.sin(n.pulse) + 1) / 2;
        const radius = n.r + glow * 2;

        // Outer glow ring
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, radius * 4);
        grad.addColorStop(0, `rgba(249,115,22,${0.25 * glow})`);
        grad.addColorStop(1, 'rgba(249,115,22,0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = i % 5 === 0
          ? `rgba(249,115,22,${0.6 + glow * 0.4})`
          : `rgba(45,90,142,${0.5 + glow * 0.3})`;
        ctx.fill();
      });

      // EEG waveform strip at bottom of canvas
      const waveY = canvas.height * 0.82;
      ctx.beginPath();
      ctx.moveTo(0, waveY);
      for (let x = 0; x < canvas.width; x += 2) {
        const freq1 = Math.sin((x / canvas.width) * Math.PI * 14 + t * 3) * 18;
        const freq2 = Math.sin((x / canvas.width) * Math.PI * 7 + t * 1.5) * 8;
        const freq3 = Math.sin((x / canvas.width) * Math.PI * 28 + t * 5) * 4;
        ctx.lineTo(x, waveY + freq1 + freq2 + freq3);
      }
      ctx.strokeStyle = 'rgba(249,115,22,0.12)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
      aria-hidden="true"
    />
  );
}

// ─── Animated EEG Waveform SVG ────────────────────────────────────────────────
function EEGWaveIcon() {
  return (
    <svg width="44" height="28" viewBox="0 0 44 28" fill="none" className="drop-shadow-[0_0_8px_rgba(249,115,22,0.8)]" aria-hidden="true">
      <path
        d="M0 14 L6 14 L9 4 L12 24 L15 10 L18 18 L21 14 L26 14 L29 2 L32 26 L35 8 L38 20 L41 14 L44 14"
        stroke="#F97316"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <animate attributeName="stroke-dasharray" values="0 200;200 0;200 0" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="stroke-dashoffset" values="200;0;-200" dur="2.4s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

// ─── Auth Gate — redirects unauthenticated users to login ────────────────────
function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/login');
    }
  }, [loading, user, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04080F] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" aria-label="Loading" />
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
}

// ─── Main Dashboard Page ──────────────────────────────────────────────────────
export default function EEGCognitiveDashboard() {
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [activePrescription, setActivePrescription] = useState<AIPrescription | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>(undefined);
  const [currentYear, setCurrentYear] = useState('');
  const [showDailyCheck, setShowDailyCheck] = useState(false);
  const [dailyCheckDone, setDailyCheckDone] = useState(false);
  const { role } = useAuth();

  useEffect(() => {
    setCurrentYear(String(new Date().getFullYear()));
  }, []);

  // Listen for session saves to capture the latest session ID
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sessionId) setActiveSessionId(detail.sessionId);
    };
    window.addEventListener('session:saved', handler);
    return () => window.removeEventListener('session:saved', handler);
  }, []);

  const handleLoaderComplete = () => {
    setLoading(false);
    // Only show the onboarding tour for non-patient roles
    if (role !== 'patient' && !localStorage.getItem('ibrain_onboarded')) {
      setShowOnboarding(true);
    }
  };

  if (loading) {
    return (
      <AuthGate>
        <IBrainLoader onComplete={handleLoaderComplete} duration={2400} />
      </AuthGate>
    );
  }

  return (
    <AuthGate>
      <SessionProvider>
        <div className="min-h-screen bg-[#04080F] flex flex-col animate-fade-in">
          {/* ── Full neural background ── */}
          <NeuralCanvas />

          {/* Deep radial vignette */}
          <div
            className="fixed inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 80% 80% at 50% 40%, transparent 25%, #04080F 100%)',
              zIndex: 1,
            }}
            aria-hidden="true"
          />

          {/* Scanline texture */}
          <div
            className="fixed inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 3px)',
              zIndex: 2,
            }}
            aria-hidden="true"
          />

          {/* Neural grid overlay */}
          <div
            className="fixed inset-0 pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(rgba(30,58,95,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,95,0.18) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
              zIndex: 2,
            }}
            aria-hidden="true"
          />

          <DashboardTopbar />

          {/* pb-20 on mobile/tablet to avoid content hidden behind bottom nav */}
          <main className="relative flex-1 px-3 sm:px-5 lg:px-8 py-3 sm:py-4 md:py-6 max-w-[80rem] mx-auto w-full pb-20 md:pb-6" style={{ zIndex: 10 }}>
            {/* Breadcrumb navigation */}
            <div className="mb-3">
              <Breadcrumb
                items={[{ label: 'EEG Cognitive Dashboard' }]}
                variant="full"
                showBackButton={true}
                backHref={role === 'patient' ? '/patient-dashboard' : role === 'clinician' ? '/clinician-portal' : role === 'admin' ? '/admin-dashboard' : '/eeg-cognitive-dashboard'}
              />
            </div>

            {/* Help / Tour button — only for non-patient roles */}
            {role !== 'patient' && (
              <div className="flex justify-end mb-3 sm:mb-4">
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-xl text-[13px] font-medium text-[#8BAFD4] hover:text-white border border-[#1E3A5F]/50 hover:border-[#F97316]/40 hover:bg-[#0F1E35] transition-all touch-manipulation active:scale-95"
                  title="Open dashboard tour"
                  aria-label="Open dashboard tour"
                >
                  <svg width="14" height="14" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                    <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M5.5 5C5.5 4.17 6.17 3.5 7 3.5C7.83 3.5 8.5 4.17 8.5 5C8.5 5.83 7 6.5 7 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    <circle cx="7" cy="9.5" r="0.6" fill="currentColor" />
                  </svg>
                  <span className="hidden sm:inline">Dashboard Tour</span>
                  <span className="sm:hidden">Tour</span>
                </button>
              </div>
            )}

            {/* ── PATIENT VIEW: simplified, focused sections only ── */}
            {role === 'patient' ? (
              <>
                {/* Daily Brain Check CTA — prominent for patients */}
                <div className="mb-4 rounded-2xl border border-[#F97316]/25 bg-[#F97316]/08 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#F97316]/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🧠</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-white">Daily Brain Check</p>
                    <p className="text-[11px] text-zinc-400">75-second habit · measurement + micro-training + insight</p>
                  </div>
                  <button
                    onClick={() => setShowDailyCheck(true)}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-bold text-white transition-all active:scale-95 min-h-[44px] flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #F97316, #EA6C0A)' }}
                  >
                    {dailyCheckDone ? '✓ Done' : 'Start'}
                  </button>
                </div>

                <CollapsibleSection title="Live Neural Feed" accentColor="orange">
                  <LiveEEGIndicator />
                </CollapsibleSection>

                <CollapsibleSection title="Signal Analysis" accentColor="navy">
                  <div className="flex flex-col xl:grid xl:grid-cols-3 gap-3 sm:gap-4">
                    <div className="xl:col-span-2"><LiveWaveformChart /></div>
                    <div className="xl:col-span-1"><BandPowerTimeline /></div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Cognitive Metrics" accentColor="orange">
                  <MetricsBentoGrid />
                </CollapsibleSection>

                <CollapsibleSection title="AI Cognitive Insights" accentColor="orange">
                  <CognitiveInsightsPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Progress Comparison" accentColor="navy">
                  <SessionProgressComparison />
                </CollapsibleSection>

                <CollapsibleSection title="Historical Trends" accentColor="navy">
                  <HistoricalTrendsChart />
                </CollapsibleSection>

                <CollapsibleSection title="Brain Health Quiz" accentColor="orange">
                  <BrainHealthQuiz />
                </CollapsibleSection>

                <CollapsibleSection title="Leaderboard & Streak Tracker" accentColor="orange">
                  <LeaderboardStreakPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Adaptive Brain Gym" accentColor="orange">
                  <AdaptiveBrainGym />
                </CollapsibleSection>

                <div data-section="ppg">
                  <CollapsibleSection title="Heart Rate & HRV Monitor" accentColor="orange" defaultOpen={false}>
                    <PPGMonitor eegSessionTag={activeSessionId} />
                  </CollapsibleSection>
                </div>
              </>
            ) : (
              <>
                {/* ── CLINICAL PRIORITY SECTIONS (always first) ── */}
                <CollapsibleSection title="Session Recording" accentColor="orange">
                  <SessionRecordingControls />
                </CollapsibleSection>

                <CollapsibleSection title="Live Neural Feed" accentColor="orange">
                  <LiveEEGIndicator />
                </CollapsibleSection>

                <CollapsibleSection title="Signal Analysis" accentColor="navy">
                  <div className="flex flex-col xl:grid xl:grid-cols-3 gap-3 sm:gap-4">
                    <div className="xl:col-span-2"><LiveWaveformChart /></div>
                    <div className="xl:col-span-1"><BandPowerTimeline /></div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Cognitive Metrics" accentColor="orange">
                  <MetricsBentoGrid />
                </CollapsibleSection>

                <CollapsibleSection title="Live Electrode Map" accentColor="orange">
                  <LiveElectrodeMap />
                </CollapsibleSection>

                <CollapsibleSection title="Channel Status & Events" accentColor="orange">
                  <div className="flex flex-col xl:grid xl:grid-cols-3 gap-3 sm:gap-4">
                    <div className="xl:col-span-2"><ChannelQualityGrid /></div>
                    <div className="xl:col-span-1"><ArtifactEventFeed /></div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="EEG Channel Alignment & Validation" accentColor="orange">
                  <EEGChannelAlignmentModule />
                </CollapsibleSection>

                {/* ── AI & BIOMARKER ANALYSIS ── */}
                <CollapsibleSection title="AI Cognitive Insights" accentColor="orange">
                  <CognitiveInsightsPanel />
                </CollapsibleSection>

                <CollapsibleSection title="AI EEG Analysis & Recommendations" accentColor="orange">
                  <AIAnalysisPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Critical Biomarker Alerts" accentColor="orange">
                  <BiomarkerNotificationSystem />
                </CollapsibleSection>

                <CollapsibleSection title="Configurable Alert Thresholds" accentColor="orange" defaultOpen={false}>
                  <AlertThresholdsConfig />
                </CollapsibleSection>

                <CollapsibleSection title="Alzheimer's Risk Biomarkers" accentColor="orange">
                  <AlzheimerBiomarkerPanel />
                </CollapsibleSection>

                <CollapsibleSection title="ADHD Risk Indicator" accentColor="orange">
                  <ADHDRiskPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Gamma Band Analysis (30–100 Hz)" accentColor="orange">
                  <GammaBandPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Functional Connectivity & Coherence Map" accentColor="orange">
                  <ConnectivityCoherenceMap />
                </CollapsibleSection>

                {/* ── PPG & WEARABLE ── */}
                <div data-section="ppg">
                  <CollapsibleSection title="PPG Heart Rate & HRV Monitor" accentColor="orange" defaultOpen={false}>
                    <PPGMonitor eegSessionTag={activeSessionId} />
                  </CollapsibleSection>
                </div>

                {/* ── HISTORICAL & LONGITUDINAL ── */}
                <CollapsibleSection title="Historical Trends" accentColor="navy">
                  <HistoricalTrendsChart />
                </CollapsibleSection>

                <CollapsibleSection title="Session History & Cognitive Trends" accentColor="orange">
                  <SessionHistoryPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Longitudinal Analytics — Cognitive Trajectory" accentColor="orange" defaultOpen={false}>
                  <LongitudinalAnalytics />
                </CollapsibleSection>

                {/* ── CLINICAL WORKFLOW ── */}
                <CollapsibleSection title="Post-Diagnostic AI Prescription" accentColor="orange" defaultOpen={false}>
                  <div className="space-y-4">
                    <PostDiagnosticPrescription
                      sessionId={activeSessionId}
                      onPrescriptionSaved={(_, p) => setActivePrescription(p)}
                    />
                    <div className="flex justify-end">
                      <ClinicianReportExport prescription={activePrescription} sessionId={activeSessionId} />
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection title="Adaptive Protocol Tracker" accentColor="orange" defaultOpen={false}>
                  <AdaptiveProtocolTracker />
                </CollapsibleSection>

                {/* ── GAMIFICATION & TRAINING ── */}
                <CollapsibleSection title="Brain Health Quiz" accentColor="orange">
                  <BrainHealthQuiz />
                </CollapsibleSection>

                <CollapsibleSection title="Leaderboard & Streak Tracker" accentColor="orange">
                  <LeaderboardStreakPanel />
                </CollapsibleSection>

                <CollapsibleSection title="Adaptive Brain Gym — Personalised Cognitive Training" accentColor="orange">
                  <AdaptiveBrainGym />
                </CollapsibleSection>

                <CollapsibleSection title="NeuroTrainer — AI Brain Rehabilitation" accentColor="orange" defaultOpen={false}>
                  <NeuroTrainer />
                </CollapsibleSection>
              </>
            )}
          </main>

          {/* Daily check modal */}
          {showDailyCheck && (
            <DailyBrainCheck
              onClose={() => setShowDailyCheck(false)}
              onComplete={() => { setDailyCheckDone(true); setShowDailyCheck(false); }}
            />
          )}

          {/* Footer — hidden on mobile (replaced by bottom nav) */}
          <footer className="relative border-t border-[#1E3A5F]/50 bg-[#04080F]/90 backdrop-blur hidden md:block" style={{ zIndex: 10 }}>
            <div className="max-w-[80rem] mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold">
                  <span className="text-[#F97316]">iBrain</span>
                  <span className="text-white"> LAB</span>
                </span>
                <span className="text-[12px] text-zinc-500 hidden sm:inline">Cogni Dashboard</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[12px] text-zinc-600 hidden md:inline">EEG Cognitive Monitoring Platform</span>
                <span className="flex items-center gap-1.5 text-[12px] text-[#F97316]/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F97316]/60 animate-pulse" aria-hidden="true" />
                  <span className="hidden sm:inline">© {currentYear} iBrain LAB</span>
                  <span className="sm:hidden">© iBrain LAB</span>
                </span>
              </div>
            </div>
          </footer>

          {/* Mobile bottom navigation — visible on phone/tablet only */}
          <div className="md:hidden" style={{ zIndex: 50 }}>
            <MobileBottomNav role={role} />
          </div>

          {/* Onboarding Tour Overlay */}
          {showOnboarding && (
            <OnboardingTour onComplete={() => setShowOnboarding(false)} />
          )}
        </div>
      </SessionProvider>
    </AuthGate>
  );
}